import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HRService from '../../../services/HrService';
import DynamicSnackbar from '../../common/Snackbar';
import CompanyService from '../../../services/CompanyService';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleIcon from '@mui/icons-material/Article';
import HomeIcon from '@mui/icons-material/Home';

const HRRecordUpload = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [importLogs, setImportLogs] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [validationErrors, setValidationErrors] = useState([]);
    const [validationWarnings, setValidationWarnings] = useState([]);
    const [isFileValid, setIsFileValid] = useState(false);

    useEffect(() => {
        // Fetch companies for dropdown
        const fetchCompanies = async () => {
            try {
                const companiesData = await CompanyService.fetchCompanies();
                setCompanies(companiesData);
            } catch (error) {
                console.error('Error fetching companies:', error);
                setSnackbarMessage('Failed to load companies');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };

        // Fetch import logs
        const fetchImportLogs = async () => {
            try {
                const logsData = await HRService.fetchHRImportLogs();
                setImportLogs(logsData);
            } catch (error) {
                console.error('Error fetching import logs:', error);
            }
        };

        fetchCompanies();
        fetchImportLogs();
    }, []);

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Parse date for validation
    const parseDate = (dateStr, dateFormats) => {
        // Handle null, empty strings, and literal "NULL" values
        if (!dateStr || dateStr.trim() === '' || dateStr.trim().toUpperCase() === 'NULL') return true;
        
        // If we can convert to YYYY-MM-DD format, it's valid
        const formatted = formatDateForServer(dateStr);
        if (formatted && formatted !== dateStr) {
            return new Date(formatted);
        }
        
        // First check if the string matches expected patterns
        const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/;
        const mmddyyyyRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
        const ddmmyyyyRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/; // Same pattern but interpreted differently
        const mmddyyRegex = /^\d{1,2}\/\d{1,2}\/\d{2}$/;
        const dmmyyRegex = /^\d{1,2}-\d{2}-\d{2}$/; // Format like 1-01-20 or 21-06-20
        
        const matchesFormat = 
            yyyymmddRegex.test(dateStr) || 
            mmddyyyyRegex.test(dateStr) ||
            ddmmyyyyRegex.test(dateStr) ||
            mmddyyRegex.test(dateStr) ||
            dmmyyRegex.test(dateStr);
            
        if (!matchesFormat) {
            return null; // Early rejection if format doesn't match expected patterns
        }
        
        // Standardize date format check to be more strict - align with backend validation
        for (const format of dateFormats) {
            try {
                // Try parsing with different formats
                const parts = dateStr.split(/[-\/]/);
                
                // If we don't have exactly 3 parts (month, day, year), it's invalid
                if (parts.length !== 3) {
                    continue;
                }
                
                let year, month, day;
                
                if (format === 'MM/DD/YYYY') {
                    month = parseInt(parts[0], 10);
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } else if (format === 'DD/MM/YYYY') {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } else if (format === 'YYYY-MM-DD') {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    day = parseInt(parts[2], 10);
                } else if (format === 'MM/DD/YY') {
                    month = parseInt(parts[0], 10);
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                }
                
                // Make sure all parts are valid numbers
                if (isNaN(year) || isNaN(month) || isNaN(day)) {
                    continue;
                }
                
                // Check if values are valid
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    const date = new Date(year, month - 1, day);
                    // Make sure the date is valid (e.g., not February 31st)
                    if (date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year) {
                        return date;
                    }
                }
            } catch (e) {
                continue;
            }
        }
        
        // Additional check for D-MM-YY format which isn't included in dateFormats
        if (/^\d{1,2}-\d{2}-\d{2}$/.test(dateStr)) {
            try {
                const parts = dateStr.split('-');
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                let year = parseInt(parts[2], 10);
                
                if (year < 100) {
                    year += year < 50 ? 2000 : 1900;
                }
                
                if (!isNaN(year) && !isNaN(month) && !isNaN(day) && 
                    month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    const date = new Date(year, month - 1, day);
                    if (date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year) {
                        return date;
                    }
                }
            } catch (e) {
                // Failed to parse this format, continue to return null
            }
        }
        
        return null;
    };

    // Validate CSV data
    const validateCSVData = async (file) => {
        setValidating(true);
        setValidationErrors([]);
        setValidationWarnings([]);
        
        const reader = new FileReader();
        
        reader.onload = (event) => {
            try {
                const content = event.target.result;
                setFileContent(content);
                
                // Split content into lines
                const lines = content.split('\n');
                if (lines.length < 2) {
                    setValidationErrors(['File appears to be empty or missing data rows']);
                    setIsFileValid(false);
                    setValidating(false);
                    return;
                }
                
                // Check headers
                const header = lines[0].split(',').map(h => h.trim().replace(/^"(.*)"$/, '$1'));
                const requiredFields = ['COMPANY_ID', 'USER_ID', 'EMAIL_ADDRESS', 'FIRST_NAME', 'LAST_NAME', 
                              'JOB_TITLE', 'DEPARTMENT', 'MANAGER', 'HIRE_DATE', 'EMP_TYPE', 
                              'STATUS'];
                              
                const missingFields = requiredFields.filter(field => !header.includes(field));
                
                if (missingFields.length > 0) {
                    setValidationErrors([`Missing required fields: ${missingFields.join(', ')}`]);
                    setIsFileValid(false);
                    setValidating(false);
                    return;
                }
                
                // Validate data rows
                const errors = [];
                const warnings = [];
                // Make sure these formats match exactly what the backend expects
                const dateFormats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'MM/DD/YY'];
                
                // Map headers to their positions
                const headerMap = {};
                header.forEach((field, index) => {
                    headerMap[field] = index;
                });
                
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue; // Skip empty lines
                    
                    const cells = lines[i].split(',').map(cell => cell.trim().replace(/^"(.*)"$/, '$1'));
                    
                    if (cells.length !== header.length) {
                        errors.push(`Row ${i}: Column count mismatch - expected ${header.length} columns but found ${cells.length}`);
                        continue;
                    }
                    
                    // Check required fields aren't empty
                    ['USER_ID', 'EMAIL_ADDRESS', 'FIRST_NAME', 'LAST_NAME', 'STATUS'].forEach(field => {
                        if (headerMap[field] !== undefined && (!cells[headerMap[field]] || cells[headerMap[field]].trim() === '')) {
                            errors.push(`Row ${i}: Missing required value for ${field}`);
                        }
                    });
                    
                    // Validate email format
                    if (headerMap['EMAIL_ADDRESS'] !== undefined) {
                        const email = cells[headerMap['EMAIL_ADDRESS']];
                        if (email && !email.match(/^[^@]+@[^@]+\.[^@]+$/)) {
                            errors.push(`Row ${i}: Invalid email format - ${email}`);
                        }
                    }
                    
                    // Validate dates
                    ['HIRE_DATE', 'REHIRE_DATE', 'TERMINATION_DATE'].forEach(dateField => {
                        if (headerMap[dateField] !== undefined && cells[headerMap[dateField]]) {
                            const dateValue = cells[headerMap[dateField]];
                            if (dateValue && dateValue.trim() !== '' && dateValue.trim().toUpperCase() !== 'NULL') {
                                // Try to parse the date using our enhanced date formatting
                                const formatted = formatDateForServer(dateValue);
                                if (!formatted) {
                                    errors.push(
                                        `Row ${i}: Invalid date format for ${dateField} - ${dateValue}. ` +
                                        `Use YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, MM/DD/YY, or D-MM-YY formats.`
                                    );
                                } else if (formatted !== dateValue && !dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                    // Add warning about date format conversion
                                    warnings.push(
                                        `Row ${i}: Date format in ${dateField} (${dateValue}) will be converted to ${formatted}`
                                    );
                                }
                            }
                        }
                    });
                    
                    // Check status is valid
                    if (headerMap['STATUS'] !== undefined) {
                        const status = cells[headerMap['STATUS']];
                        if (status && !['Active', 'Inactive', 'Terminated', 'On Leave'].includes(status)) {
                            warnings.push(`Row ${i}: Unusual status value - ${status}`);
                        }
                    }
                }
                
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setIsFileValid(errors.length === 0);
                setValidating(false);
                
                // Show summary message
                if (errors.length > 0) {
                    setSnackbarMessage(`Validation found ${errors.length} errors. Please fix before uploading. Check date formats in particular.`);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                } else if (warnings.length > 0) {
                    const dateFormatWarnings = warnings.filter(warning => warning.includes('Date format')).length;
                    let message = `File validated with ${warnings.length} warnings.`;
                    
                    if (dateFormatWarnings > 0) {
                        message += ` ${dateFormatWarnings} date formats will be automatically converted to YYYY-MM-DD.`;
                    }
                    
                    message += ' You can still upload but please review.';
                    
                    setSnackbarMessage(message);
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                } else {
                    setSnackbarMessage('File validated successfully. Ready to upload.');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                }
                
            } catch (error) {
                console.error('Error validating CSV:', error);
                setValidationErrors(['Error validating file: ' + error.message]);
                setIsFileValid(false);
                setValidating(false);
            }
        };
        
        reader.onerror = () => {
            setValidationErrors(['Error reading file']);
            setIsFileValid(false);
            setValidating(false);
        };
        
        reader.readAsText(file);
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        
        // Reset validation state
        setValidationErrors([]);
        setValidationWarnings([]);
        setIsFileValid(false);
        
        if (!selectedFile) {
            setFile(null);
            setFileContent(null);
            return;
        }
        
        if (!selectedFile.name.endsWith('.csv')) {
            setSnackbarMessage('Please upload a CSV file');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setFile(null);
            return;
        }
        
        setFile(selectedFile);
        
        // Validate the file contents
        validateCSVData(selectedFile);
    };

    const handleCompanyChange = (event) => {
        setSelectedCompany(event.target.value);
    };

    const handleDownloadTemplate = async () => {
        try {
            await HRService.downloadHRTemplate();
        } catch (error) {
            console.error('Error downloading template:', error);
            setSnackbarMessage('Failed to download template');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Convert a date to YYYY-MM-DD format
    const convertToYYYYMMDD = (dateStr, dateFormats) => {
        // Handle null, empty strings, and literal "NULL" values
        if (!dateStr || dateStr.trim() === '' || dateStr.trim().toUpperCase() === 'NULL') {
            return '';
        }
        
        for (const format of dateFormats) {
            try {
                // Try parsing with different formats
                const parts = dateStr.split(/[-\/]/);
                if (parts.length !== 3) continue;
                
                let year, month, day;
                
                if (format === 'MM/DD/YYYY') {
                    month = parseInt(parts[0], 10);
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } else if (format === 'DD/MM/YYYY') {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                } else if (format === 'YYYY-MM-DD') {
                    // Already in the right format - just validate and normalize
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    day = parseInt(parts[2], 10);
                } else if (format === 'MM/DD/YY') {
                    month = parseInt(parts[0], 10);
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                }
                
                // Make sure all parts are valid numbers
                if (isNaN(year) || isNaN(month) || isNaN(day)) {
                    continue;
                }
                
                // Check if values are valid
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    // Format to YYYY-MM-DD
                    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                }
            } catch (e) {
                continue;
            }
        }
        
        return dateStr; // Return original if can't convert (shouldn't happen due to prior validation)
    };

    // Enhanced version for server communication - always returns YYYY-MM-DD
    const formatDateForServer = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '' || dateStr.trim().toUpperCase() === 'NULL') {
            return '';
        }
        
        // If already in YYYY-MM-DD format, return as is after validation
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            // Validate parts
            const [year, month, day] = dateStr.split('-').map(part => parseInt(part, 10));
            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return dateStr;
            }
        }
        
        // Handle D-MM-YY or DD-MM-YY format (like 1-01-20 or 21-06-20)
        if (/^\d{1,2}-\d{2}-\d{2}$/.test(dateStr)) {
            try {
                const [day, month, year] = dateStr.split('-').map(part => parseInt(part, 10));
                let fullYear = year;
                if (year < 100) {
                    fullYear = year < 50 ? 2000 + year : 1900 + year;
                }
                
                // Validate the date
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    // Check if it's a valid date by creating a Date object and comparing values
                    const testDate = new Date(fullYear, month - 1, day);
                    if (testDate.getMonth() === month - 1 && testDate.getDate() === day) {
                        return `${fullYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                    }
                }
            } catch (error) {
                console.error('Error parsing D-MM-YY format:', error);
            }
        }
        
        // Try using the standard convertToYYYYMMDD with all supported formats
        const dateFormats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'MM/DD/YY'];
        const formattedDate = convertToYYYYMMDD(dateStr, dateFormats);
        
        // If conversion succeeded, return the formatted date
        if (formattedDate !== dateStr) {
            return formattedDate;
        }
        
        // Last resort: try to use Date object
        try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
        } catch (error) {
            console.error('Error formatting date for server:', error);
        }
        
        return ''; // Return empty string for invalid dates
    };

    const handleUpload = async () => {
        if (!file) {
            setSnackbarMessage('Please select a file to upload');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        if (!isFileValid) {
            setSnackbarMessage('Please fix validation errors before uploading');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);

        try {
            // If the file is valid, but we need to ensure all dates are in YYYY-MM-DD format,
            // we'll parse the CSV, convert the dates, and create a new file
            if (fileContent) {
                const lines = fileContent.split('\n');
                
                if (lines.length > 1) {
                    const header = lines[0].split(',').map(h => h.trim().replace(/^"(.*)"$/, '$1'));
                    const headerMap = {};
                    
                    // Map headers to their positions
                    header.forEach((field, index) => {
                        headerMap[field] = index;
                    });
                    
                    // Date columns to standardize
                    const dateColumns = ['HIRE_DATE', 'REHIRE_DATE', 'TERMINATION_DATE'];
                    
                    // Process data rows
                    for (let i = 1; i < lines.length; i++) {
                        if (!lines[i].trim()) continue;
                        
                        // Split the line into cells
                        const cells = lines[i].split(',').map(cell => cell.trim().replace(/^"(.*)"$/, '$1'));
                        
                        // Convert date fields to YYYY-MM-DD
                        dateColumns.forEach(column => {
                            if (headerMap[column] !== undefined && cells[headerMap[column]]) {
                                const dateValue = cells[headerMap[column]];
                                if (dateValue && dateValue.trim() !== '' && dateValue.trim().toUpperCase() !== 'NULL') {
                                    cells[headerMap[column]] = formatDateForServer(dateValue);
                                }
                            }
                        });
                        
                        // Rebuild the line
                        lines[i] = cells.join(',');
                    }
                    
                    // Create a new file with the converted data
                    const convertedContent = lines.join('\n');
                    const convertedFile = new File([convertedContent], file.name, { type: 'text/csv' });
                    
                    // Create form data with converted file
                    const formData = new FormData();
                    formData.append('file', convertedFile);
                    if (selectedCompany) {
                        formData.append('company_id', selectedCompany);
                    }
                    
                    // Upload the converted file
                    const response = await HRService.uploadHRRecords(formData);
                    setSnackbarMessage('HR records uploaded successfully');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    
                    // Refresh import logs
                    const logsData = await HRService.fetchHRImportLogs();
                    setImportLogs(logsData);
                } else {
                    throw new Error('Invalid file format');
                }
            } else {
                // Fallback to original upload if file content is not available
                const formData = new FormData();
                formData.append('file', file);
                if (selectedCompany) {
                    formData.append('company_id', selectedCompany);
                }
                
                const response = await HRService.uploadHRRecords(formData);
                setSnackbarMessage('HR records uploaded successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                
                // Refresh import logs
                const logsData = await HRService.fetchHRImportLogs();
                setImportLogs(logsData);
            }
            
            // Reset form
            setFile(null);
            setFileContent(null);
            setValidationErrors([]);
            setValidationWarnings([]);
            setIsFileValid(false);
            document.getElementById('hr-file-upload').value = '';
        } catch (error) {
            console.error('Error uploading HR records:', error);
            let errorMessage = 'Failed to upload HR records';
            
            // Extract error message from various possible formats
            if (error.message) {
                errorMessage = error.message;
            } else if (error.response && error.response.data) {
                if (error.response.data.detail && Array.isArray(error.response.data.detail)) {
                    // Join validation errors
                    errorMessage = 'Validation errors: ' + error.response.data.detail.join(', ');
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else {
                    errorMessage = JSON.stringify(error.response.data);
                }
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else if (error instanceof Object) {
                errorMessage = 'Error: ' + JSON.stringify(error);
            }
            
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && dateString.trim().toUpperCase() === 'NULL') return '';
        
        // First, standardize to YYYY-MM-DD if not already
        let standardDate = dateString;
        
        // Check if we need to convert to standard format first
        if (typeof dateString === 'string' && 
            !dateString.match(/^\d{4}-\d{2}-\d{2}$/) && 
            (dateString.includes('/') || dateString.includes('-'))) {
            const dateFormats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'MM/DD/YY'];
            standardDate = convertToYYYYMMDD(dateString, dateFormats);
        }
        
        try {
            // Try to create a valid Date object
            let date;
            
            if (typeof standardDate === 'string' && standardDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // If we have YYYY-MM-DD format, parse it directly
                const [year, month, day] = standardDate.split('-').map(p => parseInt(p, 10));
                date = new Date(year, month - 1, day);
            } else {
                // Otherwise use standard Date parsing
                date = new Date(standardDate);
            }
            
            // If date is valid, format it for display
            if (!isNaN(date.getTime())) {
                return date.toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
            
            // Manual parsing for formats like 1/1/20
            if (typeof dateString === 'string') {
                const parts = dateString.split('/');
                if (parts.length === 3) {
                    let year = parseInt(parts[2], 10);
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                    
                    const month = parseInt(parts[0], 10) - 1;
                    const day = parseInt(parts[1], 10);
                    
                    const manualDate = new Date(year, month, day);
                    if (!isNaN(manualDate.getTime())) {
                        return manualDate.toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        });
                    }
                }
            }
            
            // Return original if we can't parse it
            return dateString;
        } catch (error) {
            console.error('Error formatting date for display:', error);
            return dateString || 'Unknown';
        }
    };

    const DateFormatCollapsible = () => {
        const [expanded, setExpanded] = useState(false);
        
        return (
            <mui.Paper 
                variant="outlined" 
                sx={{ 
                    mb: 3, 
                    borderColor: expanded ? 'info.main' : 'rgba(0, 0, 0, 0.12)',
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'border-color 0.3s ease'
                }}
            >
                <mui.Tooltip title={expanded ? "Click to collapse" : "Click to expand date format info"}>
                    <mui.Box 
                        onClick={() => setExpanded(!expanded)}
                        sx={{ 
                            p: 1.5, 
                            bgcolor: expanded ? 'rgba(41, 182, 246, 0.1)' : 'rgba(0, 0, 0, 0.02)', 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s ease',
                            '&:hover': { 
                                bgcolor: expanded ? 'rgba(41, 182, 246, 0.15)' : 'rgba(0, 0, 0, 0.05)' 
                            }
                        }}
                    >
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <mui.Box sx={{ color: 'info.main', mr: 1, display: 'flex' }}>
                                <InfoIcon fontSize="small" />
                            </mui.Box>
                            <mui.Typography variant="body2" fontWeight="medium">
                                Supported Date Formats
                                <mui.Typography 
                                    component="span" 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    (YYYY-MM-DD recommended)
                                </mui.Typography>
                            </mui.Typography>
                        </mui.Box>
                        
                        {expanded ? (
                            <ExpandLessIcon fontSize="small" />
                        ) : (
                            <ExpandMoreIcon fontSize="small" />
                        )}
                    </mui.Box>
                </mui.Tooltip>
                
                <mui.Collapse in={expanded} timeout={300}>
                    <mui.Box sx={{ p: 2 }}>
                        <mui.Grid container spacing={2}>
                            <mui.Grid item xs={12} sm={6}>
                                <mui.Typography variant="subtitle2" gutterBottom>
                                    Supported Formats:
                                </mui.Typography>
                                <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.6' }}>
                                    <li><strong>YYYY-MM-DD</strong> (e.g., 2023-01-15)</li>
                                    <li>MM/DD/YYYY (e.g., 01/15/2023)</li>
                                    <li>DD/MM/YYYY (e.g., 15/01/2023)</li>
                                    <li>MM/DD/YY (e.g., 01/15/23)</li>
                                    <li>D-MM-YY (e.g., 1-01-20)</li>
                                </ul>
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} sm={6}>
                                <mui.Typography variant="subtitle2" gutterBottom>
                                    Important Notes:
                                </mui.Typography>
                                <mui.Box component="ul" sx={{ m: 0, pl: 2.5, lineHeight: '1.6' }}>
                                    <li>Empty fields and "NULL" values are accepted</li>
                                    <li>All dates will be converted to YYYY-MM-DD format</li>
                                    <li>You'll see warnings when dates are converted</li>
                                </mui.Box>
                            </mui.Grid>
                            
                            <mui.Grid item xs={12}>
                                <mui.Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                                    This standardization ensures consistent date storage and prevents data issues.
                                </mui.Typography>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Box>
                </mui.Collapse>
            </mui.Paper>
        );
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                        Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/hr-data-management">
                        HR Data
                    </mui.Link>
                    <mui.Typography color="text.primary">Manual Upload</mui.Typography>
                </mui.Breadcrumbs>

                <SearchAppBar title="HR Record Upload" icon={<ContactMailIcon />} />
                <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
                    Manually upload HR records from CSV files
                </mui.Typography>

                <Separator />

                <mui.Grid container spacing={3} sx={{ mt: 2 }}>
                    <mui.Grid item xs={12} md={6}>
                        <mui.Paper elevation={3} sx={{ p: 3 }}>
                            <mui.Typography variant="h6" gutterBottom>
                                Upload HR Records
                            </mui.Typography>
                            
                            <mui.Button 
                                variant="outlined" 
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadTemplate}
                                sx={{ mb: 3 }}
                            >
                                Download Template
                            </mui.Button>
                            
                            <DateFormatCollapsible />
                            
                            <mui.FormControl fullWidth sx={{ mb: 3 }}>
                                <mui.InputLabel id="company-select-label">Company</mui.InputLabel>
                                <mui.Select
                                    labelId="company-select-label"
                                    value={selectedCompany}
                                    label="Company"
                                    onChange={handleCompanyChange}
                                >
                                    <mui.MenuItem value="">
                                        <em>Select a company</em>
                                    </mui.MenuItem>
                                    {companies.map((company) => (
                                        <mui.MenuItem key={company.id} value={company.id}>
                                            {company.COMPANY_NAME}
                                        </mui.MenuItem>
                                    ))}
                                </mui.Select>
                                <mui.FormHelperText>
                                    Select the company for these HR records
                                </mui.FormHelperText>
                            </mui.FormControl>
                            
                            <mui.Box sx={{ mb: 3 }}>
                                <mui.Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<UploadFileIcon />}
                                >
                                    Select CSV File
                                    <input
                                        id="hr-file-upload"
                                        type="file"
                                        accept=".csv"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </mui.Button>
                                <mui.Typography variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                    {file ? (
                                        <>
                                            {isFileValid ? (
                                                <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
                                            ) : validationErrors.length > 0 ? (
                                                <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                                            ) : validationWarnings.length > 0 ? (
                                                <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                                            ) : null}
                                            {`Selected file: ${file.name}`}
                                            {validating && <mui.CircularProgress size={16} sx={{ ml: 1 }} />}
                                        </>
                                    ) : (
                                        'No file selected'
                                    )}
                                </mui.Typography>
                            </mui.Box>
                            
                            {/* File validation results */}
                            {file && (validationErrors.length > 0 || validationWarnings.length > 0) && (
                                <mui.Box sx={{ mb: 3, mt: 2 }}>
                                    {validationErrors.length > 0 && (
                                        <mui.Alert severity="error" sx={{ mb: 2 }}>
                                            <mui.AlertTitle>Validation Errors</mui.AlertTitle>
                                            <mui.List dense>
                                                {validationErrors.slice(0, 5).map((error, index) => (
                                                    <mui.ListItem key={index}>
                                                        <mui.ListItemText primary={error} />
                                                    </mui.ListItem>
                                                ))}
                                                {validationErrors.length > 5 && (
                                                    <mui.ListItem>
                                                        <mui.ListItemText primary={`... and ${validationErrors.length - 5} more errors`} />
                                                    </mui.ListItem>
                                                )}
                                            </mui.List>
                                        </mui.Alert>
                                    )}
                                    
                                    {validationWarnings.length > 0 && (
                                        <mui.Alert severity="warning">
                                            <mui.AlertTitle>Warnings</mui.AlertTitle>
                                            <mui.List dense>
                                                {validationWarnings.slice(0, 3).map((warning, index) => (
                                                    <mui.ListItem key={index}>
                                                        <mui.ListItemText primary={warning} />
                                                    </mui.ListItem>
                                                ))}
                                                {validationWarnings.length > 3 && (
                                                    <mui.ListItem>
                                                        <mui.ListItemText primary={`... and ${validationWarnings.length - 3} more warnings`} />
                                                    </mui.ListItem>
                                                )}
                                            </mui.List>
                                        </mui.Alert>
                                    )}
                                </mui.Box>
                            )}
                            
                            {file && isFileValid && (
                                <mui.Alert severity="success" sx={{ mb: 3 }}>
                                    File validated successfully. Ready to upload.
                                </mui.Alert>
                            )}
                            
                            <mui.Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpload}
                                disabled={loading || !file || !isFileValid || validating}
                                sx={{ mt: 2 }}
                            >
                                {loading ? (
                                    <mui.CircularProgress size={24} />
                                ) : (
                                    'Upload Records'
                                )}
                            </mui.Button>
                        </mui.Paper>
                    </mui.Grid>

                    <mui.Grid item xs={12} md={6}>
                        <mui.Paper elevation={3} sx={{ p: 3 }}>
                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <mui.Typography variant="h6">
                                    Import History
                                </mui.Typography>
                                {importLogs.length > 5 && (
                                    <mui.Button 
                                        size="small"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate('/interfaces/hr/details')}
                                    >
                                        View All
                                    </mui.Button>
                                )}
                            </mui.Box>
                            
                            {importLogs.length > 0 ? (
                                <mui.List sx={{ p: 0 }}>
                                    {importLogs.slice(0, 5).map((log) => (
                                        <mui.ListItem 
                                            key={log.id}
                                            disableGutters
                                            divider
                                            sx={{ 
                                                px: 1, 
                                                py: 1.2,
                                                borderRadius: '4px', 
                                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' }
                                            }}
                                        >
                                            <mui.ListItemIcon sx={{ minWidth: 40 }}>
                                                {log.JOB_COMPLETE ? (
                                                    <mui.Avatar 
                                                        sx={{ 
                                                            width: 32, 
                                                            height: 32, 
                                                            bgcolor: 'success.light',
                                                            color: 'success.contrastText' 
                                                        }}
                                                    >
                                                        <CheckCircleOutlineIcon fontSize="small" />
                                                    </mui.Avatar>
                                                ) : (
                                                    <mui.Avatar 
                                                        sx={{ 
                                                            width: 32, 
                                                            height: 32, 
                                                            bgcolor: 'error.light',
                                                            color: 'error.contrastText' 
                                                        }}
                                                    >
                                                        <ErrorOutlineIcon fontSize="small" />
                                                    </mui.Avatar>
                                                )}
                                            </mui.ListItemIcon>
                                            
                                            <mui.ListItemText
                                                primary={
                                                    <mui.Typography 
                                                        variant="body2" 
                                                        fontWeight="medium" 
                                                        component="div"
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                    >
                                                        {log.JOB_FILE_NAME || log.FILE_NAME || 'Unknown File'}
                                                        <mui.Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                                            <mui.Chip
                                                                label={`${log.SOURCE_LINE_COUNT || 0} rows`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ height: 20, fontSize: '0.7rem', mr: 1 }}
                                                            />
                                                            <mui.Chip
                                                                label={`${log.DESTI_LINE_COUNT || 0} records`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        </mui.Box>
                                                    </mui.Typography>
                                                }
                                                secondary={
                                                    <mui.Typography variant="caption" color="text.secondary">
                                                        {formatDate(log.JOB_DATE || log.IMPORT_DATE)}
                                                    </mui.Typography>
                                                }
                                            />
                                            
                                            <mui.Chip 
                                                label={log.JOB_COMPLETE ? "Complete" : "Failed"}
                                                color={log.JOB_COMPLETE ? "success" : "error"}
                                                size="small"
                                                variant="outlined"
                                                sx={{ minWidth: 75, fontWeight: 500 }}
                                            />
                                        </mui.ListItem>
                                    ))}
                                </mui.List>
                            ) : (
                                <mui.Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        py: 4,
                                        opacity: 0.7
                                    }}
                                >
                                    <mui.Box sx={{ mb: 2, color: 'text.disabled' }}>
                                        <ArticleIcon sx={{ fontSize: 48 }} />
                                    </mui.Box>
                                    <mui.Typography variant="body2" color="text.secondary" gutterBottom>
                                        No import history available
                                    </mui.Typography>
                                    <mui.Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 2 }}>
                                        Upload your first HR records file to see history
                                    </mui.Typography>
                                </mui.Box>
                            )}
                        </mui.Paper>
                    </mui.Grid>
                </mui.Grid>
            </ResponsiveContainer>

            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default HRRecordUpload; 